import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../scss/load.scss';
import BaseURL from '../assets/contants/BaseURL';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWeightHanging,
  faChartLine,
  faTachometerAlt,
  faExclamationTriangle,
  faFilter,
  faRedo,
  faSyncAlt,
  faChevronLeft,
  faChevronRight,
  faUserCircle,
  faCaretDown,
  faUser,
  faSignOutAlt,
  faCrown,
  faInbox,
  faCheckCircle,
  faExclamationCircle,
  faInfoCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const Load = () => {
  const navigate = useNavigate();
  const [cranes, setCranes] = useState([]);
  const [selectedCrane, setSelectedCrane] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState('today');
  
  // State for applied filters (used for actual data fetching)
  const [appliedCrane, setAppliedCrane] = useState('');
  const [appliedDateRange, setAppliedDateRange] = useState('today');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [metrics, setMetrics] = useState({
    currentLoad: '--',
    averageCapacity: '--',
    maxLoad: '--',
    overallStatus: 'Normal'
  });
  const [chartData, setChartData] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState({ cranes: [], overall: 'disconnected', message: 'Connecting...' });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [orgLogo, setOrgLogo] = useState(null);

  const widgetsWebSocketRef = useRef(null);
  const graphWebSocketRef = useRef(null);
  const tableWebSocketRef = useRef(null);
  const reconnectTimeoutsRef = useRef({ widgets: null, graph: null, table: null });
  const reconnectAttemptsRef = useRef({ widgets: 0, graph: 0, table: 0 });
  const isUnmountingRef = useRef(false);
  
  // Refs to store current values for use in WebSocket callbacks without causing reconnections
  const currentPageRef = useRef(currentPage);
  const appliedCraneRef = useRef(appliedCrane);
  const appliedDateRangeRef = useRef(appliedDateRange);

  // Update refs when values change
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    appliedCraneRef.current = appliedCrane;
  }, [appliedCrane]);

  useEffect(() => {
    appliedDateRangeRef.current = appliedDateRange;
  }, [appliedDateRange]);

  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');
  const getSessionID = () => sessionStorage.getItem('session_id') || localStorage.getItem('session_id');

  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);

  // Get WebSocket host from BaseURL
  const getWebSocketHost = () => {
    let host = BaseURL;
    if (BaseURL.includes('://')) {
      host = BaseURL.split('://')[1];
    }
    if (host.endsWith('/')) {
      host = host.slice(0, -1);
    }
    return host;
  };

  const getWebSocketUrl = (endpoint) => {
    const token = getAuthToken();
    const session_id = getSessionID();

    if (!token || !session_id) {
      console.warn(`Missing auth credentials for ${endpoint} WebSocket`);
      return null;
    }

    const host = getWebSocketHost();
    const wsScheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsScheme}//${host}/data/${endpoint}/?token=${encodeURIComponent(token)}&session_id=${encodeURIComponent(session_id)}`;
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      if (typeof timestamp === 'string' && timestamp.includes('-')) {
        return timestamp;
      }
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString();
      }
      return timestamp;
    } catch (e) {
      return timestamp;
    }
  };

  const getStatusClass = (percentage) => {
    const percent = parseFloat(percentage);
    if (percent >= 90) return 'danger';
    if (percent >= 75) return 'warning';
    return 'normal';
  };

  const getStatusText = (percentage) => {
    const percent = parseFloat(percentage);
    if (percent >= 90) return 'Critical';
    if (percent >= 75) return 'Warning';
    return 'Normal';
  };

  const getChartColor = (index) => {
    const colors = ['#3498db', '#2ecc71', '#e74c3c', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#34495e'];
    return colors[index % colors.length];
  };

  const clearReconnectTimeout = (type) => {
    if (reconnectTimeoutsRef.current[type]) {
      clearTimeout(reconnectTimeoutsRef.current[type]);
      reconnectTimeoutsRef.current[type] = null;
    }
  };

  const renderTable = useCallback((data) => {
    let records = [];
    
    if (data && data.data && Array.isArray(data.data)) {
      records = data.data;
    } else if (Array.isArray(data)) {
      records = data;
    } else {
      setTableData([]);
      return;
    }

    const formattedRecords = records.map(item => ({
      timestamp: formatTimestamp(item.timestamp || item.actual_timestamp || item.created_at || 'N/A'),
      craneId: item.crane_id || item.crane || item.machine_id || 'N/A',
      operation: item.operation || item.operation_type || 'Lift',
      load: item.load !== undefined && item.load !== null ? parseFloat(item.load).toFixed(1) : '0.0',
      capacity: item.capacity !== undefined && item.capacity !== null ? parseFloat(item.capacity).toFixed(1) : '0.0',
      percentage: item.percentage !== undefined && item.percentage !== null ? parseFloat(item.percentage).toFixed(1) : '0.0',
      status: getStatusText(item.percentage),
      statusClass: getStatusClass(item.percentage)
    }));
    
    setTableData(formattedRecords);
  }, []);

  const updatePaginationFromResponse = useCallback((pagination) => {
    if (pagination) {
      setTotalRecords(pagination.total_count || 0);
      setTotalPages(pagination.total_pages || 1);
      setCurrentPage(pagination.page || 1);
    } else {
      setTotalRecords(0);
      setTotalPages(1);
      setCurrentPage(1);
    }
  }, []);

  const updateWidgetsData = useCallback((data) => {
    if (data.error) return;

    setMetrics({
      currentLoad: data.current_load !== undefined ? data.current_load.toFixed(1) : '--',
      averageCapacity: data.average_capacity !== undefined ? data.average_capacity.toFixed(1) : '--',
      maxLoad: data.today_max_load !== undefined ? data.today_max_load.toFixed(1) : '--',
      overallStatus: data.overload_count > 0 ? 'Alert' : 'Normal'
    });
  }, []);

  const updateConnectionStatusUI = useCallback((statusData) => {
    if (!statusData || !statusData.cranes || statusData.cranes.length === 0) {
      setConnectionStatus({
        cranes: [],
        overall: 'disconnected',
        message: 'No cranes available'
      });
      return;
    }

    const cranesList = statusData.cranes;
    const onlineCount = cranesList.filter(crane => crane.connection_status === 'online').length;
    const totalCount = cranesList.length;

    let overall = 'disconnected';
    let message = '';

    if (totalCount === 0) {
      overall = 'disconnected';
      message = 'No cranes available';
    } else if (onlineCount === totalCount) {
      overall = 'connected';
      message = 'Connected';
    } else if (onlineCount > 0) {
      overall = 'partial';
      message = `${onlineCount}/${totalCount} cranes Connected`;
    } else {
      overall = 'disconnected';
      message = 'Disconnected';
    }

    setConnectionStatus({ cranes: cranesList, overall, message });
  }, []);

  const refreshTableData = useCallback(() => {
    if (tableWebSocketRef.current && tableWebSocketRef.current.readyState === WebSocket.OPEN) {
      tableWebSocketRef.current.send(JSON.stringify({
        type: 'filter',
        crane: appliedCraneRef.current,
        date_range: appliedDateRangeRef.current,
        page: currentPageRef.current,
        page_size: pageSize
      }));
    }
  }, [pageSize]);

  // Connect Widgets WebSocket
  const connectWidgetsWebSocket = useCallback(() => {
    if (isUnmountingRef.current) return;

    const wsUrl = getWebSocketUrl('load-lift-widgets');
    if (!wsUrl) {
      console.error('Cannot connect Widgets WebSocket: Missing credentials');
      return;
    }

    clearReconnectTimeout('widgets');

    const ws = new WebSocket(wsUrl);
    widgetsWebSocketRef.current = ws;

    const connectionTimeout = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        console.error('Widgets WebSocket connection timeout');
        ws.close();
      }
    }, 10000);

    ws.onopen = () => {
      clearTimeout(connectionTimeout);
      reconnectAttemptsRef.current.widgets = 0;
      console.log('Load Lift Widgets WebSocket connected');
      
      ws.send(JSON.stringify({
        type: 'filter',
        crane: appliedCraneRef.current,
        date_range: appliedDateRangeRef.current
      }));
    };

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'pong') return;
        if (data.type === 'widgets_update') {
          updateWidgetsData(data.data);
          if (data.connection_status) {
            updateConnectionStatusUI(data.connection_status);
          }
        }
      } catch (error) {
        console.error('Error parsing widgets message:', error);
      }
    };

    ws.onclose = (event) => {
      clearTimeout(connectionTimeout);
      console.log(`Widgets WebSocket disconnected (code: ${event.code})`);
      widgetsWebSocketRef.current = null;

      if (!isUnmountingRef.current && event.code !== 1000 && reconnectAttemptsRef.current.widgets < 5) {
        const delay = Math.min(3000 * Math.pow(2, reconnectAttemptsRef.current.widgets), 30000);
        reconnectAttemptsRef.current.widgets++;
        
        clearReconnectTimeout('widgets');
        reconnectTimeoutsRef.current.widgets = setTimeout(() => {
          connectWidgetsWebSocket();
        }, delay);
      }
    };

    ws.onerror = (error) => {
      clearTimeout(connectionTimeout);
      console.error('Widgets WebSocket error:', error);
    };
  }, [updateWidgetsData, updateConnectionStatusUI]);

  // Connect Graph WebSocket
  const connectGraphWebSocket = useCallback(() => {
    if (isUnmountingRef.current) return;

    const wsUrl = getWebSocketUrl('load-lift-graph');
    if (!wsUrl) {
      console.error('Cannot connect Graph WebSocket: Missing credentials');
      return;
    }

    clearReconnectTimeout('graph');

    const ws = new WebSocket(wsUrl);
    graphWebSocketRef.current = ws;

    const connectionTimeout = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        console.error('Graph WebSocket connection timeout');
        ws.close();
      }
    }, 10000);

    ws.onopen = () => {
      clearTimeout(connectionTimeout);
      reconnectAttemptsRef.current.graph = 0;
      console.log('Load Lift Graph WebSocket connected');
      
      ws.send(JSON.stringify({
        type: 'filter',
        crane: appliedCraneRef.current,
        date_range: appliedDateRangeRef.current
      }));
    };

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'pong') return;
        if (data.type === 'graph_update') {
          setChartData(data.data);
        }
      } catch (error) {
        console.error('Error parsing graph message:', error);
      }
    };

    ws.onclose = (event) => {
      clearTimeout(connectionTimeout);
      console.log(`Graph WebSocket disconnected (code: ${event.code})`);
      graphWebSocketRef.current = null;

      if (!isUnmountingRef.current && event.code !== 1000 && reconnectAttemptsRef.current.graph < 5) {
        const delay = Math.min(3000 * Math.pow(2, reconnectAttemptsRef.current.graph), 30000);
        reconnectAttemptsRef.current.graph++;
        
        clearReconnectTimeout('graph');
        reconnectTimeoutsRef.current.graph = setTimeout(() => {
          connectGraphWebSocket();
        }, delay);
      }
    };

    ws.onerror = (error) => {
      clearTimeout(connectionTimeout);
      console.error('Graph WebSocket error:', error);
    };
  }, []);

  // Connect Table WebSocket - uses refs for values that change
  const connectTableWebSocket = useCallback(() => {
    if (isUnmountingRef.current) return;

    const wsUrl = getWebSocketUrl('load-lift-table');
    if (!wsUrl) {
      console.error('Cannot connect Table WebSocket: Missing credentials');
      return;
    }

    clearReconnectTimeout('table');

    const ws = new WebSocket(wsUrl);
    tableWebSocketRef.current = ws;

    const connectionTimeout = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        console.error('Table WebSocket connection timeout');
        ws.close();
      }
    }, 10000);

    ws.onopen = () => {
      clearTimeout(connectionTimeout);
      reconnectAttemptsRef.current.table = 0;
      console.log('Load Lift Table WebSocket connected');
      
      // Use refs to get current values
      ws.send(JSON.stringify({
        type: 'filter',
        crane: appliedCraneRef.current,
        date_range: appliedDateRangeRef.current,
        page: currentPageRef.current,
        page_size: pageSize
      }));
    };

    ws.onmessage = (e) => {
      setIsLoading(false);
      try {
        const response = JSON.parse(e.data);
        if (response.type === 'pong') return;
        if (response.type === 'table_update') {
          renderTable(response.data);
          if (response.pagination) {
            updatePaginationFromResponse(response.pagination);
          }
        }
      } catch (error) {
        console.error('Error parsing table message:', error);
        setIsLoading(false);
      }
    };

    ws.onclose = (event) => {
      clearTimeout(connectionTimeout);
      console.log(`Table WebSocket disconnected (code: ${event.code})`);
      tableWebSocketRef.current = null;

      if (!isUnmountingRef.current && event.code !== 1000 && reconnectAttemptsRef.current.table < 5) {
        const delay = Math.min(3000 * Math.pow(2, reconnectAttemptsRef.current.table), 30000);
        reconnectAttemptsRef.current.table++;
        
        clearReconnectTimeout('table');
        reconnectTimeoutsRef.current.table = setTimeout(() => {
          connectTableWebSocket();
        }, delay);
      }
    };

    ws.onerror = (error) => {
      clearTimeout(connectionTimeout);
      console.error('Table WebSocket error:', error);
      setIsLoading(false);
    };
  }, [pageSize, renderTable, updatePaginationFromResponse]);

  const loadCraneOptions = useCallback(async () => {
    const token = getAuthToken();

    if (!token) {
      console.error('No authentication token found');
      showNotification('Please log in again to access crane data', 'error');
      return;
    }

    try {
      const response = await fetch(`${BaseURL}meters/cms-cranes/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        showNotification('Session expired. Please log in again.', 'error');
        setTimeout(() => navigate('/login'), 1500);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const craneList = Array.isArray(data) ? data : (data.results || data.cranes || []);
        setCranes(craneList);
        if (craneList.length > 0) {
          const firstCrane = craneList[0].machine_id;
          setSelectedCrane(firstCrane);
          setAppliedCrane(firstCrane);
          setAppliedDateRange('today');
        }
      } else {
        console.error('Failed to fetch cranes');
      }
    } catch (error) {
      console.error('Error fetching cranes:', error);
    }
  }, [navigate]);

  // Apply filters - reset to page 1
  const applyFilters = useCallback(() => {
    setAppliedCrane(selectedCrane);
    setAppliedDateRange(selectedDateRange);
    setCurrentPage(1);
    showNotification('Filters applied', 'success');
  }, [selectedCrane, selectedDateRange]);

  // Reset filters - reset to page 1
  const resetFilters = useCallback(() => {
    if (cranes.length > 0) {
      const defaultCrane = cranes[0].machine_id;
      setSelectedCrane(defaultCrane);
      setSelectedDateRange('today');
      setAppliedCrane(defaultCrane);
      setAppliedDateRange('today');
      setCurrentPage(1);
      showNotification('Filters reset to default', 'success');
    }
  }, [cranes]);

  const refreshAllData = useCallback(() => {
    if (widgetsWebSocketRef.current?.readyState === WebSocket.OPEN) {
      widgetsWebSocketRef.current.send(JSON.stringify({
        type: 'refresh',
        crane: appliedCraneRef.current,
        date_range: appliedDateRangeRef.current
      }));
    }

    if (graphWebSocketRef.current?.readyState === WebSocket.OPEN) {
      graphWebSocketRef.current.send(JSON.stringify({
        type: 'refresh',
        crane: appliedCraneRef.current,
        date_range: appliedDateRangeRef.current
      }));
    }

    refreshTableData();
    showNotification('Refreshing data with current filters...', 'success');
  }, [refreshTableData]);

  // FIXED: Pagination - Go to previous page
  const goToPreviousPage = () => {
    if (currentPage <= 1) return;
    setCurrentPage(currentPage - 1);
  };

  // FIXED: Pagination - Go to next page
  const goToNextPage = () => {
    if (currentPage >= totalPages) return;
    setCurrentPage(currentPage + 1);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      isUnmountingRef.current = true;
      
      if (widgetsWebSocketRef.current) widgetsWebSocketRef.current.close(1000, 'User logout');
      if (graphWebSocketRef.current) graphWebSocketRef.current.close(1000, 'User logout');
      if (tableWebSocketRef.current) tableWebSocketRef.current.close(1000, 'User logout');
      
      localStorage.removeItem('isLoggedIn');
      sessionStorage.removeItem('Token');
      sessionStorage.removeItem('session_id');
      showNotification('Logged out successfully!', 'success');
      setTimeout(() => navigate('/login'), 1500);
    }
  };

  // Load crane options on mount
  useEffect(() => {
    isUnmountingRef.current = false;
    loadCraneOptions();

    return () => {
      isUnmountingRef.current = true;
      
      Object.keys(reconnectTimeoutsRef.current).forEach(key => {
        if (reconnectTimeoutsRef.current[key]) {
          clearTimeout(reconnectTimeoutsRef.current[key]);
        }
      });
      
      if (widgetsWebSocketRef.current) widgetsWebSocketRef.current.close(1000, 'Component unmount');
      if (graphWebSocketRef.current) graphWebSocketRef.current.close(1000, 'Component unmount');
      if (tableWebSocketRef.current) tableWebSocketRef.current.close(1000, 'Component unmount');
    };
  }, [loadCraneOptions]);

  // FIXED: Connect WebSockets only when filters change (not on page change)
  useEffect(() => {
    if (appliedCrane && !isInitialLoad) {
      // Close existing connections before creating new ones
      if (widgetsWebSocketRef.current) widgetsWebSocketRef.current.close(1000, 'Filters changed');
      if (graphWebSocketRef.current) graphWebSocketRef.current.close(1000, 'Filters changed');
      if (tableWebSocketRef.current) tableWebSocketRef.current.close(1000, 'Filters changed');
      
      // Give time for connections to close before reopening
      setTimeout(() => {
        connectWidgetsWebSocket();
        connectGraphWebSocket();
        connectTableWebSocket();
      }, 100);
    } else if (appliedCrane && isInitialLoad) {
      // Initial connection
      connectWidgetsWebSocket();
      connectGraphWebSocket();
      connectTableWebSocket();
      setIsInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedCrane, appliedDateRange]); // Only depend on filter values, not on connection functions

  // FIXED: Handle page changes without reconnecting WebSockets
  useEffect(() => {
    if (tableWebSocketRef.current?.readyState === WebSocket.OPEN && appliedCrane) {
      // Just send a new filter message with updated page
      tableWebSocketRef.current.send(JSON.stringify({
        type: 'filter',
        crane: appliedCraneRef.current,
        date_range: appliedDateRangeRef.current,
        page: currentPage,
        page_size: pageSize
      }));
      setIsLoading(true);
    }
  }, [currentPage, appliedCrane, pageSize]); // Only trigger on page changes

  const getChartConfig = () => {
    if (!chartData || !chartData.datasets) return null;

    const datasets = chartData.datasets.map((dataset, index) => ({
      label: dataset.label || `Crane ${index + 1}`,
      data: Array.isArray(dataset.data) ? dataset.data : [],
      borderColor: dataset.borderColor || getChartColor(index),
      backgroundColor: dataset.backgroundColor || getChartColor(index) + '20',
      borderWidth: dataset.borderWidth || 3,
      tension: dataset.tension || 0.4,
      pointBackgroundColor: dataset.pointBackgroundColor || getChartColor(index),
      pointBorderColor: dataset.pointBorderColor || '#fff',
      pointBorderWidth: dataset.pointBorderWidth || 2,
      pointRadius: dataset.pointRadius || 4,
      pointHoverRadius: dataset.pointHoverRadius || 6,
      fill: dataset.fill !== undefined ? dataset.fill : true
    }));

    let maxValue = 100;
    chartData.datasets.forEach(dataset => {
      if (dataset.data && Array.isArray(dataset.data)) {
        const maxDataValue = Math.max(...dataset.data.filter(v => v !== null && v !== undefined), 0);
        maxValue = Math.max(maxValue, Math.ceil(maxDataValue / 10) * 10);
      }
    });

    return {
      labels: chartData.labels || [],
      datasets,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
          y: {
            beginAtZero: true,
            max: maxValue,
            title: { display: true, text: 'Load Percentage (%)', font: { weight: 'bold', size: 13 } },
            ticks: { callback: (value) => value + '%', stepSize: maxValue > 100 ? Math.ceil(maxValue / 10) : 20 }
          },
          x: { 
            title: { display: true, text: 'Time of Day', font: { weight: 'bold', size: 13 } }, 
            grid: { display: false } 
          }
        },
        plugins: {
          legend: { position: 'top', labels: { usePointStyle: true, pointStyle: 'circle' } },
          tooltip: { callbacks: { label: (context) => `${context.dataset.label || ''}: ${context.raw}%` } }
        }
      }
    };
  };

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);

  return (
    <div className="load-lift-log">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div className={`connection-status ${connectionStatus.overall}`}>
              <span className={`status-indicator ${connectionStatus.overall}`}></span>
              <span className="status-text">{connectionStatus.message}</span>
            </div>

            <div className="user-info-wrapper">
              <div className="account-dropdown">
                <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                  {orgLogo ? (
                                    <img
                                      src={orgLogo}
                                      alt="Organization Logo"
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '2px solid #4361ee'
                                      }}
                                      onError={() => setOrgLogo(null)}
                                    />
                                  ) : (
                                    <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                  )}
                  <span>{sessionStorage.getItem('username') || 'Innospace'}</span>
                  <FontAwesomeIcon icon={faCaretDown} />
                </div>
                {isDropdownOpen && (
                  <div className="account-menu show">
                    <div className="account-menu-item plan-info">
                      <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                      <span>Basic Plan</span>
                    </div>
                    <div className="account-menu-divider"></div>
                    <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faUser} /> Profile
                    </button>
                    <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                    </button>
                    <div className="account-menu-divider"></div>
                    <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                      <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page Title */}
        <div className="page-titles">
          <h1>Load Lift Log</h1>
          <p>Records of all load weight measurements</p>
        </div>

        {/* Metrics Cards */}
        <div className="metrics-container">
          <div className="metric-card">
            <div className="metric-title"><FontAwesomeIcon icon={faWeightHanging} /> Current Load</div>
            <div className="metric-value">{metrics.currentLoad} <span>kg</span></div>
          </div>
          <div className="metric-card">
            <div className="metric-title"><FontAwesomeIcon icon={faChartLine} /> Average Capacity</div>
            <div className="metric-value">{metrics.averageCapacity} <span>%</span></div>
          </div>
          <div className="metric-card">
            <div className="metric-title"><FontAwesomeIcon icon={faTachometerAlt} /> Today Max Load</div>
            <div className="metric-value">{metrics.maxLoad} <span>kg</span></div>
          </div>
          <div className="metric-card">
            <div className="metric-title"><FontAwesomeIcon icon={faExclamationTriangle} /> Overall Status</div>
            <div className="metric-value" style={{ color: metrics.overallStatus === 'Alert' ? '#e74c3c' : '#2ecc71' }}>
              {metrics.overallStatus}
            </div>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="log-controls">
          <div className="filter-group">
            <label className="filter-label">Crane</label>
            <select className="filter-selects" value={selectedCrane} onChange={(e) => setSelectedCrane(e.target.value)}>
              {cranes.map(crane => (
                <option key={crane.machine_id} value={crane.machine_id}>
                  {crane.machine_name} ({crane.machine_id})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Date Range</label>
            <select className="filter-selects" value={selectedDateRange} onChange={(e) => setSelectedDateRange(e.target.value)}>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
          </div>

          <div className="action-buttonsg" style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            flexWrap: 'nowrap', 
            gap: '10px',
            alignItems: 'center',
            marginTop: '30px'
          }}>
            <button className="btn btn-primary" onClick={applyFilters}>
              <FontAwesomeIcon icon={faFilter} /> Apply Filters
            </button>
            <button className="btn btn-secondary" onClick={resetFilters}>
              <FontAwesomeIcon icon={faRedo} /> Reset
            </button>
            <button className="btn btn-refresh" onClick={refreshAllData}>
              <FontAwesomeIcon icon={faSyncAlt} /> Refresh
            </button>
          </div>
        </div>

        {/* Chart Container */}
        <div className="chart-containerc">
          <h3 style={{ marginBottom: '15px', color: '#2c3e50' }}>Crane Load Comparison</h3>
          <div style={{ height: '350px' }}>
            {chartData ? (
              <Line 
                data={getChartConfig()?.datasets ? { labels: getChartConfig().labels, datasets: getChartConfig().datasets } : { labels: [], datasets: [] }} 
                options={getChartConfig()?.options} 
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#95a5a6' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', marginRight: '10px' }} />
                Loading chart data...
              </div>
            )}
          </div>
        </div>

        {/* Load Log Table */}
        <div className="log-table-container">
          {isLoading && (
            <div className="table-loading-overlay">
              <div className="loading-spinner"></div>
            </div>
          )}
          <table className="log-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Crane ID</th>
                <th>Operation</th>
                <th>Load (kg)</th>
                <th>Capacity</th>
                <th>Percentage</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tableData.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                    <FontAwesomeIcon icon={faInbox} style={{ fontSize: '2rem', color: '#95a5a6', marginBottom: '10px' }} />
                    <p>No data found for the selected filters</p>
                  </td>
                </tr>
              ) : (
                tableData.map((row, index) => (
                  <tr key={index} className="new-row">
                    <td>{row.timestamp}</td>
                    <td>{row.craneId}</td>
                    <td>{row.operation}</td>
                    <td>{row.load} kg</td>
                    <td>{row.capacity} kg</td>
                    <td>{row.percentage}%</td>
                    <td><span className={`load-status status-${row.statusClass}`}>{row.status}</span></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination-wrapper">
            <div className="records-info">
              {totalRecords > 0 ? `Showing ${startRecord} to ${endRecord} of ${totalRecords} records` : 'Showing 0 of 0 records'}
            </div>
            <div className="pagination-controls">
              <button 
                className="btn btn-secondary" 
                onClick={goToPreviousPage} 
                disabled={currentPage <= 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} /> Previous
              </button>
              <span className="page-info">Page {currentPage} of {totalPages || 1}</span>
              <button 
                className="btn btn-secondary" 
                onClick={goToNextPage} 
                disabled={currentPage >= totalPages}
              >
                Next <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification.show && (
          <div className={`notification notification-${notification.type} show`}>
            <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : notification.type === 'error' ? faExclamationCircle : faInfoCircle} />
            {notification.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default Load;